(() => {
  'use strict';

  const PAGE_SIZE = 250;
  const MODEL_KEY = 'filter.p.m.custom.vehicle_model';
  const BRAND_KEY = 'filter.p.m.custom.vehicle_brand';
  const LOGO_KEY = 'filter.p.m.custom.logo_type';
  const LEGACY_MODEL_KEY = 'acura_model';
  const LEGACY_PAGE_KEYS = ['acura_page', 'acura_finish'];
  const FINISH_SWITCH_KEY = 'finish_switch';
  const COMPACT_COLLECTION_PATHS = new Set([
    '/collections/led-door-sills',
    '/collections/led-door-sills-pro',
    '/collections/stainless-door-sills'
  ]);
  const CATALOG_SELECTOR = '[data-matte-catalog]';
  const EXCLUDED_PRODUCT_IDS = new Set([
    '8641640628381', '8641640693917', '8641640890525', '8641641185437',
    '8641641578653', '8641642889373', '8641643446429', '8642420965533',
    '8642420998301', '8642421063837', '8642421096605', '8642421129373',
    '8642421457053', '8642421522589', '8642421555357', '8642428240029',
    '9030680379549', '9030680969373', '9030681362589', '9036268404893',
    '9045083390109', '9049392021661', '9049783828637', '9049785139357',
    '9049904513181', '9049904611485', '9049904742557', '9049904775325',
    '9050458423453', '9050458456221', '9050458488989'
  ]);
  const instances = new WeakMap();
  const metadataCache = new Map();
  const brandRowsCache = new Map();
  const brandRequests = new Map();
  const brandDirectoryCache = new Map();
  const METADATA_CONCURRENCY = 3;
  let listenersBound = false;
  let touchGesture = null;
  let suppressedSummary = null;

  const asString = value => value == null ? '' : String(value).trim();
  const lower = value => asString(value).toLocaleLowerCase();
  const unique = values => [...new Set(values.filter(Boolean))];
  const family = value => asString(value).split(/\s+/)[0] || '';
  const isAcuraBrand = value => lower(value) === 'acura';
  const MERCEDES_CANONICAL = 'Mercedes-Benz';
  const MERCEDES_ALIASES = new Set(['Mercedes', MERCEDES_CANONICAL]);
  const isMercedesBrand = value => MERCEDES_ALIASES.has(asString(value)) ||
    ['mercedes', 'mercedes-benz'].includes(lower(value));
  const compactAliasGroups = [
    ['Mercedes-Benz', ['Mercedes', 'Mercedes-Benz']],
    ['Kia', ['Kia', 'KIA']],
    ['RAM', ['RAM', 'Ram']],
    ['Škoda', ['Skoda', 'Škoda']]
  ];
  const compactAliasGroup = value => compactAliasGroups.find(([, names]) => names.some(name => lower(name) === lower(value)));
  const canonicalCompactBrand = value => compactAliasGroup(value)?.[0] || asString(value);
  const compactBrandValues = (state, value) => state.brandDirectory?.get(canonicalCompactBrand(value))?.values || compactAliasGroup(value)?.[1] || [asString(value)];
  const compactBrandMatches = (row, brand) => row.brands.some(value => canonicalCompactBrand(value) === canonicalCompactBrand(brand));
  const compactNativeBrandMatches = (row, brand) => row.brands.includes(asString(brand));
  const isElement = value => value && value.nodeType === 1;
  const pathFor = value => {
    try { return new URL(value || '/', 'https://invalid.local/').pathname.replace(/\/$/, '') || '/'; }
    catch (_) { return ''; }
  };
  const isCompactCollection = (collection, collectionURL) => {
    const handle = asString(collection).replace(/^\//, '').replace(/^collections\//, '');
    return COMPACT_COLLECTION_PATHS.has(pathFor(collectionURL)) ||
      COMPACT_COLLECTION_PATHS.has(`/collections/${handle}`);
  };

  /* A metafield may be scalar or a list. Do not split scalar models: a value
     such as "Grand Cherokee" or "Model 3" is one exact native value. */
  const values = value => {
    if (Array.isArray(value)) return unique(value.flatMap(values));
    if (value && typeof value === 'object') {
      if ('value' in value) return values(value.value);
      if ('values' in value) return values(value.values);
    }
    const text = asString(value);
    return text ? [text] : [];
  };

  const normalizeRow = raw => {
    const row = Array.isArray(raw) ? {
      id: raw[0],
      brand: raw[1],
      model: raw[2],
      logo: raw[3]
    } : (raw && typeof raw === 'object' ? raw : {});
    return {
      id: asString(row.id ?? row.product_id ?? row.productId),
      brands: values(row.brand ?? row.brands ?? row.vehicle_brand),
      models: values(row.model ?? row.models ?? row.vehicle_model),
      logos: values(row.logo ?? row.logos ?? row.logo_type)
    };
  };

  const normalizeRows = rows => (Array.isArray(rows) ? rows : [])
    .map(normalizeRow)
    .filter(row => row.id && !EXCLUDED_PRODUCT_IDS.has(row.id));

  const modelGroup = (brand, model) => isAcuraBrand(brand) ? family(model) : asString(model);
  const modelKey = (brand, model) => `${asString(brand)}\u0000${modelGroup(brand, model)}`;

  const buildModelOptions = (rows, context = {}) => {
    const globalCatalog = context.globalCatalog === true || context.global === true;
    const fallbackBrand = asString(context.brand);
    const compactMercedesAliases = context.compactMercedesAliases === true;
    const byKey = new Map();
    normalizeRows(rows).forEach(row => {
      const brands = row.brands.length ? row.brands : (fallbackBrand ? [fallbackBrand] : ['']);
      brands.forEach(brand => row.models.forEach(model => {
        const cleanBrand = compactMercedesAliases ? canonicalCompactBrand(brand) : asString(brand);
        const cleanModel = asString(model);
        if (!cleanModel) return;
        const group = modelGroup(cleanBrand, cleanModel);
        const key = modelKey(cleanBrand, cleanModel);
        let option = byKey.get(key);
        if (!option) {
          option = {
            key,
            brand: cleanBrand,
            group,
            acura: isAcuraBrand(cleanBrand),
            values: new Set(),
            rowIds: new Set(),
            label: globalCatalog && cleanBrand ? `${cleanBrand} ${group}` : group
          };
          if (compactMercedesAliases) option.compactMercedesAliases = true;
          byKey.set(key, option);
        }
        option.values.add(cleanModel);
        option.rowIds.add(row.id);
      }));
    });
    return [...byKey.values()]
      .map(option => ({ ...option, values: [...option.values], rowIds: [...option.rowIds] }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' }));
  };

  const rowMatchesOption = (row, option, fallbackBrand = '') => {
    if (!option) return false;
    const brandMatches = !option.brand || (option.compactMercedesAliases
      ? compactBrandMatches(row, option.brand)
      : row.brands.includes(option.brand)) ||
      (!row.brands.length && option.brand === asString(fallbackBrand));
    if (!brandMatches) return false;
    return row.models.some(model => modelGroup(option.brand, model) === option.group);
  };

  const rowsForOption = (rows, option, fallbackBrand = '') => normalizeRows(rows)
    .filter(row => rowMatchesOption(row, option, fallbackBrand));

  const nativeTokens = value => unique(asString(value).split(',').map(asString));
  const optionMatchesNativeModel = (option, nativeValue) => {
    const raw = asString(nativeValue);
    if (!raw) return false;
    const tokens = nativeTokens(raw);
    if (option.acura) return tokens.some(token => option.values.includes(token) || family(token) === option.group);
    return option.values.includes(raw) || (tokens.length === 1 && option.values.includes(tokens[0]));
  };

  const optionByURL = (options, url, globalCatalog) => {
    const modelValue = url.searchParams.get(MODEL_KEY) || url.searchParams.get(LEGACY_MODEL_KEY) || '';
    const brands = nativeTokens(url.searchParams.get(BRAND_KEY) || '');
    if (!modelValue) return null;
    return options.find(option => {
      if (globalCatalog && option.brand && brands.length && !brands.includes(option.brand)) return false;
      return optionMatchesNativeModel(option, modelValue);
    }) || null;
  };

  const allLogos = rows => unique(normalizeRows(rows).flatMap(row => row.logos))
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));

  const logosForOption = (rows, option, context = {}) => {
    if (!option) return allLogos(rows);
    return unique(rowsForOption(rows, option, context.brand).flatMap(row => row.logos))
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
  };

  const resolveSelection = (rows, options, context = {}, inputURL = '') => {
    const url = inputURL instanceof URL ? new URL(inputURL.href) : new URL(inputURL || 'https://invalid.local/');
    const normalizedRows = normalizeRows(rows);
    const globalCatalog = context.globalCatalog === true || context.global === true;
    const modelOptions = options || buildModelOptions(normalizedRows, context);
    let modelOption = optionByURL(modelOptions, url, globalCatalog);
    if (!modelOption && context.modelScope === true && modelOptions.length === 1) modelOption = modelOptions[0];
    if (!modelOption && !globalCatalog && modelOptions.length === 1 && context.autoSingleton !== false) modelOption = modelOptions[0];
    const logos = logosForOption(normalizedRows, modelOption, context);
    const logoParam = asString(url.searchParams.get(LOGO_KEY));
    let logo = logos.includes(logoParam) ? logoParam : '';
    if (!logo && logos.length === 1 && context.autoSingleton !== false) logo = logos[0];
    if (!modelOption && !globalCatalog) {
      const allLogos = unique(normalizedRows.flatMap(row => row.logos));
      if (allLogos.length === 1 && context.fixedBrand !== false) {
        logo = allLogos[0];
      }
    }
    return { url, modelOptions, modelOption, logos, logo, globalCatalog };
  };

  const canonicalizeURL = (inputURL, selection, context = {}) => {
    const url = inputURL instanceof URL ? new URL(inputURL.href) : new URL(inputURL || 'https://invalid.local/');
    const modelScope = context.modelScope === true;
    const globalCatalog = context.globalCatalog === true || context.global === true;
    for (const key of [...url.searchParams.keys()]) if (key.startsWith('filter.')) url.searchParams.delete(key);
    url.searchParams.delete(LEGACY_MODEL_KEY);
    LEGACY_PAGE_KEYS.forEach(key => url.searchParams.delete(key));
    if (modelScope || !selection?.modelOption) {
      url.searchParams.delete(MODEL_KEY);
      url.searchParams.delete(BRAND_KEY);
      if (selection?.logo && selection.logos?.includes(selection.logo)) url.searchParams.set(LOGO_KEY, selection.logo);
      else url.searchParams.delete(LOGO_KEY);
      return url;
    }
    const option = selection.modelOption;
    const valuesForNative = option.acura ? option.values : [option.group];
    url.searchParams.delete(MODEL_KEY);
    valuesForNative.forEach(value => url.searchParams.append(MODEL_KEY, value));
    if (option.brand) url.searchParams.set(BRAND_KEY, option.brand);
    else if (globalCatalog) url.searchParams.delete(BRAND_KEY);
    if (selection.logo && selection.logos.includes(selection.logo)) url.searchParams.set(LOGO_KEY, selection.logo);
    else url.searchParams.delete(LOGO_KEY);
    return url;
  };

  /* Compact finish collections keep the three native facets independent. The
     metadata rows are the complete current collection, so counts can be
     calculated locally while Shopify remains the source of the product grid
     and pagination. */
  const sortedValues = values => unique(values)
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));

  const compactUniqueRows = rows => {
    const seen = new Set();
    return normalizeRows(rows).filter(row => {
      if (seen.has(row.id)) return false;
      seen.add(row.id);
      return true;
    });
  };

  const compactBrandParams = url => unique(url.searchParams.getAll(BRAND_KEY)
    .flatMap(value => nativeTokens(value)));

  const compactBrandOptions = rows => sortedValues(compactUniqueRows(rows)
    .flatMap(row => row.brands).map(canonicalCompactBrand));

  const compactModelValues = url => unique(url.searchParams.getAll(MODEL_KEY)
    .flatMap(value => nativeTokens(value)));

  const compactModelOptionFor = (options, modelValues, brand = '') => {
    if (!modelValues.length) return null;
    const matches = option => modelValues.some(value => optionMatchesNativeModel(option, value));
    const canonicalBrand = canonicalCompactBrand(brand);
    return (canonicalBrand ? options.find(option => option.brand === canonicalBrand && matches(option)) : options.find(matches)) || null;
  };

  const compactUnknownModelOption = (modelValues, brand = '') => {
    const cleanValues = unique(modelValues);
    if (!cleanValues.length) return null;
    return {
      key: cleanValues.join('\u0000'),
      brand: asString(brand),
      group: cleanValues.join(', '),
      acura: isAcuraBrand(brand),
      values: cleanValues,
      rowIds: [],
      label: cleanValues.join(', '),
      compactMercedesAliases: Boolean(compactAliasGroup(brand)),
      unknown: true
    };
  };

  const compactRowsForSelection = (rows, selection, omit = '') => {
    const seen = new Set();
    return normalizeRows(rows)
    .filter(row => {
      const brandValues = selection.brandValues?.length ? selection.brandValues :
        (selection.hasBrand ? [selection.brand] : []);
      if (omit !== 'brand' && selection.hasBrand &&
        !(selection.brandValues?.length
          ? brandValues.some(brand => compactNativeBrandMatches(row, brand))
          : brandValues.some(brand => compactBrandMatches(row, brand)))) return false;
      if (omit !== 'model' && selection.hasModel) {
        if (selection.modelUnknown || !selection.modelOption || selection.modelOption.unknown ||
          !rowMatchesOption(row, selection.modelOption)) return false;
      }
      if (omit !== 'logo' && selection.hasLogo &&
        (selection.logoUnknown || !row.logos.includes(selection.logo))) return false;
      return true;
    })
    .filter(row => { if (seen.has(row.id)) return false; seen.add(row.id); return true; });
  };

  const compactCount = (rows, selection, field, option) => compactRowsForSelection(rows, selection, field)
    .filter(row => {
      if (field === 'brand') {
        if (compactAliasGroup(option) && selection.brandValues?.length === 1 && canonicalCompactBrand(selection.brandValues[0]) === canonicalCompactBrand(option)) {
          return compactNativeBrandMatches(row, selection.brandValues[0]);
        }
        return compactBrandMatches(row, option);
      }
      if (field === 'logo') return row.logos.includes(option);
      return option && !option.unknown && rowMatchesOption(row, option);
    }).length;

  const resolveCompactSelection = (rows, inputURL = '') => {
    const url = inputURL instanceof URL ? new URL(inputURL.href) : new URL(inputURL || 'https://invalid.local/');
    const normalizedRows = normalizeRows(rows);
    const modelOptions = buildModelOptions(normalizedRows, { globalCatalog: true, compactMercedesAliases: true });
    const brands = compactBrandOptions(normalizedRows);
    const brandValues = compactBrandParams(url);
    const rawBrand = asString(brandValues[0]);
    const brand = canonicalCompactBrand(rawBrand);
    const modelValues = compactModelValues(url);
    const modelOption = compactModelOptionFor(modelOptions, modelValues, brand);
    const modelUnknown = modelValues.length > 0 && !modelOption;
    const selectedModel = modelOption || compactUnknownModelOption(modelValues, brand);
    const logo = asString(url.searchParams.get(LOGO_KEY));
    const logosWithoutLogo = sortedValues(compactRowsForSelection(normalizedRows, {
      hasBrand: Boolean(brand), brand,
      brandValues,
      hasModel: modelValues.length > 0, modelOption: selectedModel, modelUnknown,
      hasLogo: false, logo: '', logoUnknown: false
    }, 'logo').flatMap(row => row.logos));
    const logoUnknown = Boolean(logo) && !logosWithoutLogo.includes(logo);
    const selection = {
      url,
      globalCatalog: true,
      brands,
      brand,
      brandValues,
      brandParams: url.searchParams.getAll(BRAND_KEY),
      brandKnown: !brandValues.length || brandValues.every(value => brands.includes(canonicalCompactBrand(value))),
      hasBrand: Boolean(brand),
      modelOptions,
      modelOption: selectedModel,
      modelValues,
      modelUnknown,
      hasModel: modelValues.length > 0,
      logo,
      logoUnknown,
      hasLogo: Boolean(logo),
      logos: logosWithoutLogo,
      noResults: false
    };
    selection.noResults = !compactRowsForSelection(normalizedRows, selection).length &&
      (selection.hasBrand || selection.hasModel || selection.hasLogo);
    return selection;
  };

  const compactSelectionOptions = (rows, selection) => {
    const normalizedRows = normalizeRows(rows);
    const brands = sortedValues(compactRowsForSelection(normalizedRows, selection, 'brand')
      .flatMap(row => row.brands).map(canonicalCompactBrand));
    if (selection.hasBrand && !brands.includes(selection.brand)) brands.push(selection.brand);
    const allModels = buildModelOptions(compactRowsForSelection(normalizedRows, selection, 'model'), {
      globalCatalog: true,
      compactMercedesAliases: true
    });
    if (selection.modelOption && !allModels.some(option => option.key === selection.modelOption.key)) {
      allModels.push(selection.modelOption);
    }
    allModels.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' }));
    const logos = sortedValues(compactRowsForSelection(normalizedRows, selection, 'logo').flatMap(row => row.logos));
    if (selection.hasLogo && !logos.includes(selection.logo)) logos.push(selection.logo);
    return { brands: sortedValues(brands), models: allModels, logos: sortedValues(logos) };
  };

  const compactNativeModelValues = option => option?.acura ? option.values : [option?.group].filter(Boolean);

  const compactCanonicalizeFinishSwitchURL = (inputURL, rows) => {
    const url = inputURL instanceof URL ? new URL(inputURL.href) : new URL(inputURL || 'https://invalid.local/');
    const selection = resolveCompactSelection(rows, url);
    const incomingBrands = compactBrandParams(url);
    const nativeBrands = new Set(compactUniqueRows(rows).flatMap(row => row.brands));
    const retainedBrands = incomingBrands.filter(value => nativeBrands.has(value));
    const retainedBrand = canonicalCompactBrand(retainedBrands[0] || '');
    const candidateRows = retainedBrands.length ? compactUniqueRows(rows).filter(row => retainedBrands.some(value => compactNativeBrandMatches(row, value))) : rows;
    const options = buildModelOptions(candidateRows, { globalCatalog: true, compactMercedesAliases: true });
    const modelCandidates = retainedBrand ? options.filter(option => option.brand === canonicalCompactBrand(retainedBrand)) : options;
    const retainedModel = selection.hasModel && (!selection.hasBrand || retainedBrands.length) ?
      compactModelOptionFor(modelCandidates, selection.modelValues, retainedBrand) : null;
    const retainedLogoRows = compactUniqueRows(rows).filter(row => {
      if (selection.hasBrand && !retainedBrands.length) return true;
      if (retainedBrands.length && !retainedBrands.some(value => compactNativeBrandMatches(row, value))) return false;
      return !retainedModel || rowMatchesOption(row, retainedModel);
    });
    const retainedLogos = sortedValues(retainedLogoRows.flatMap(row => row.logos));
    const retainedLogo = selection.logo && retainedLogos.includes(selection.logo) ? selection.logo : '';
    url.searchParams.delete(BRAND_KEY);
    url.searchParams.delete(MODEL_KEY);
    url.searchParams.delete(LOGO_KEY);
    retainedBrands.forEach(value => url.searchParams.append(BRAND_KEY, value));
    if (retainedModel) compactNativeModelValues(retainedModel).forEach(value => url.searchParams.append(MODEL_KEY, value));
    if (retainedLogo) url.searchParams.set(LOGO_KEY, retainedLogo);
    url.searchParams.delete(FINISH_SWITCH_KEY);
    url.searchParams.delete('page');
    return url;
  };

  const componentURLChanged = (before, after) => {
    const a = before instanceof URL ? before : new URL(before);
    const b = after instanceof URL ? after : new URL(after);
    return a.pathname !== b.pathname || a.search !== b.search || a.hash !== b.hash;
  };

  const closestCatalog = target => {
    if (!isElement(target)) return null;
    const catalog = target.closest(CATALOG_SELECTOR);
    return catalog || null;
  };

  const ensureState = (catalog, carry = null) => {
    if (!catalog) return null;
    const current = instances.get(catalog);
    if (current) {
      current.form = catalog.querySelector('[data-matte-filters]');
      return current;
    }
    const form = catalog.querySelector('[data-matte-filters]');
    if (!form) return null;
    const state = {
      root: catalog,
      form,
      metadata: carry?.metadata || null,
      metadataPromise: carry?.metadataPromise || null,
      metadataObserved: null,
      metadataCacheKey: carry?.metadataCacheKey || '',
      metadataController: null,
      brandDirectory: carry?.brandDirectory || null,
      compactRequest: 0,
      compactInitialized: false,
      autoSingleton: carry?.autoSingleton !== false,
      brandDirectoryController: null,
      ready: Boolean(carry?.metadata),
      currentURL: carry?.currentURL ? new URL(carry.currentURL.href) : new URL(window.location.href),
      pendingURL: null,
      resultController: null,
      selection: null,
      destroyed: false
    };
    instances.set(catalog, state);
    return state;
  };

  const config = state => {
    const form = state?.form;
    const root = state?.root;
    const collection = form?.dataset.matteCollection || root?.dataset.matteCollection || '';
    const collectionURL = form?.dataset.matteCollectionUrl || root?.dataset.matteCollectionUrl || window.location.pathname;
    const modelScope = (root?.dataset.matteModelScope || form?.dataset.matteModelScope) === 'true';
    const globalCatalog = (root?.dataset.matteGlobal || form?.dataset.matteGlobal) === 'true' || collection === 'led-door-sills-pro';
    const compactFinishCatalog = (root?.dataset.matteCompactFinishCatalog || form?.dataset.matteCompactFinishCatalog) === 'true' &&
      isCompactCollection(collection, collectionURL);
    const sectionId = root?.dataset.matteSectionId || form?.dataset.matteSectionId ||
      (root?.closest('[id^="shopify-section-"]')?.id || '').replace(/^shopify-section-/, '');
    const metadataURL = form?.dataset.matteMetadataUrl || root?.dataset.matteMetadataUrl || '';
    const metadataSectionId = form?.dataset.matteMetadataSectionId || root?.dataset.matteMetadataSectionId || '';
    const brand = form?.dataset.matteBrand || root?.dataset.matteBrand || '';
    return { collection, collectionURL, modelScope, globalCatalog, compactFinishCatalog, sectionId, metadataURL, metadataSectionId, brand };
  };

  const metadataBaseURL = state => {
    const settings = config(state);
    const explicit = Boolean(settings.metadataURL);
    const raw = settings.metadataURL || settings.collectionURL || window.location.href;
    const url = new URL(raw, window.location.href);
    const current = new URL(state.currentURL?.href || window.location.href);
    for (const key of [...url.searchParams.keys()]) {
      if (key.startsWith('filter.') || key === 'page' || key === 'sort_by' || key === FINISH_SWITCH_KEY || key === LEGACY_MODEL_KEY || LEGACY_PAGE_KEYS.includes(key)) {
        url.searchParams.delete(key);
      }
    }
    ['preview_theme_id', 'view'].forEach(key => {
      if (!url.searchParams.has(key) && current.searchParams.has(key)) url.searchParams.set(key, current.searchParams.get(key));
    });
    if (!explicit && settings.metadataSectionId) url.searchParams.set('section_id', settings.metadataSectionId);
    if (settings.compactFinishCatalog) {
      compactBrandParams(current).sort().forEach(value => url.searchParams.append(BRAND_KEY, value));
    }
    url.searchParams.set('page', '1');
    return url;
  };

  const parseMetadataResponse = async response => {
    if (!response.ok) throw new Error(`Metadata response ${response.status}`);
    const body = await response.text();
    let parsed = null;
    try { parsed = JSON.parse(body); } catch (_) { /* Section Rendering API returns HTML. */ }
    if (!parsed && typeof DOMParser !== 'undefined') {
      const documentFragment = new DOMParser().parseFromString(body, 'text/html');
      const script = documentFragment.querySelector('script[data-matte-filter-values],script[data-catalog-filter-values]');
      if (script) {
        try { parsed = JSON.parse(script.textContent || ''); } catch (_) { /* handled below */ }
      }
    }
    if (!parsed || typeof parsed !== 'object') throw new Error('Invalid metadata response');
    return parsed;
  };

  const metadataPageURL = (base, page) => {
    const url = new URL(base.href);
    url.searchParams.set('page', String(page));
    return url;
  };

  const fetchMetadata = async state => {
    const base = metadataBaseURL(state);
    const controller = new AbortController();
    state.metadataController = controller;
    const first = await parseMetadataResponse(await fetch(metadataPageURL(base, 1), {
      credentials: 'same-origin',
      signal: controller.signal
    }));
    const total = Number(first.total);
    const pages = Number(first.pages);
    if (!Number.isInteger(total) || total < 0 || !Number.isInteger(pages) || pages < 1) throw new Error('Invalid metadata totals');
    const expectedPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    if (pages !== expectedPages || Number(first.page) !== 1 || !Array.isArray(first.rows) || first.rows.length > PAGE_SIZE) {
      throw new Error('Incomplete metadata pagination');
    }
    if (pages > 1 && first.rows.length !== PAGE_SIZE) throw new Error('Incomplete metadata page');
    const rows = [];
    const seen = new Set();
    let excludedRows = 0;
    const appendPage = (envelope, expectedPage) => {
      if (Number(envelope.page) !== expectedPage || Number(envelope.pages) !== pages || Number(envelope.total) !== total || !Array.isArray(envelope.rows)) {
        throw new Error('Inconsistent metadata pagination');
      }
      if (envelope.rows.length > PAGE_SIZE || (expectedPage < pages && envelope.rows.length !== PAGE_SIZE)) {
        throw new Error('Incomplete metadata page');
      }
      excludedRows += envelope.rows.filter(raw => {
        const row = normalizeRow(raw);
        return row.id && EXCLUDED_PRODUCT_IDS.has(row.id);
      }).length;
      normalizeRows(envelope.rows).forEach(row => {
        if (!row.id || seen.has(row.id)) throw new Error('Duplicate metadata row');
        seen.add(row.id);
        rows.push(row);
      });
    };
    appendPage(first, 1);
    let nextPage = 2;
    const envelopes = new Map();
    try {
      await Promise.all(Array.from({ length: Math.min(config(state).compactFinishCatalog ? METADATA_CONCURRENCY : 1, pages - 1) }, async () => {
        while (nextPage <= pages) {
          const page = nextPage++;
          const envelope = await parseMetadataResponse(await fetch(metadataPageURL(base, page), {
            credentials: 'same-origin', signal: controller.signal
          }));
          envelopes.set(page, envelope);
        }
      }));
    } catch (error) {
      controller.abort();
      throw error;
    }
    for (let page = 2; page <= pages; page++) appendPage(envelopes.get(page), page);
    if (rows.length + excludedRows !== total) throw new Error(`Incomplete metadata rows (${rows.length + excludedRows}/${total})`);
    return {
      collection: asString(first.collection),
      total: rows.length,
      pages: Math.max(1, Math.ceil(rows.length / PAGE_SIZE)),
      pageSize: PAGE_SIZE,
      rows
    };
  };

  const setSummaryState = (control, stateName, disabled) => {
    if (!control) return;
    control.dataset.state = stateName;
    const summary = control.querySelector('summary');
    if (!summary) return;
    summary.setAttribute('aria-disabled', String(Boolean(disabled)));
    summary.tabIndex = disabled ? -1 : 0;
    if (disabled) control.open = false;
  };

  const setStatus = (state, status, message = '', retryURL = null, retryKind = 'metadata') => {
    const statusNode = state.form?.querySelector('[data-matte-status]');
    if (!statusNode) return;
    const textNode = statusNode.querySelector('[data-matte-status-text]');
    const retry = statusNode.querySelector('[data-matte-retry]');
    statusNode.dataset.matteStatusState = status;
    if (textNode) textNode.textContent = message;
    if (retry) {
      retry.hidden = !retryURL;
      if (retryURL) {
        retry.dataset.matteRetryUrl = retryURL.href;
        retry.dataset.matteRetryKind = retryKind;
      } else {
        delete retry.dataset.matteRetryUrl;
        delete retry.dataset.matteRetryKind;
      }
    }
    statusNode.hidden = !message;
  };

  const setPending = state => {
    if (config(state).compactFinishCatalog) {
      renderCompact(state);
      setStatus(state, 'loading', 'Loading vehicle options…');
      return;
    }
    state.form?.querySelectorAll('[data-matte-filter-search]').forEach(input => { input.disabled = true; });
    state.form?.querySelectorAll('[data-matte-control]').forEach(control => setSummaryState(control, 'loading', true));
    setStatus(state, 'loading', 'Loading vehicle options…');
  };

  const optionCount = (rows, option, context) => rowsForOption(rows, option, context.brand).length;
  const labelForModel = option => option?.label || '';

  const modelResultCount = (state, option, settings) => {
    const rows = rowsForOption(state.metadata.rows, option, settings.brand);
    const logo = state.selection?.logo;
    if (logo && rows.some(row => row.logos.includes(logo))) return rows.filter(row => row.logos.includes(logo)).length;
    return rows.length;
  };

  const renderOptions = (state, control, options, selectedValue, field, optionData = null) => {
    const optionBox = control?.querySelector('[data-matte-options]');
    const search = control?.querySelector('[data-matte-filter-search]');
    if (!optionBox) return;
    optionBox.replaceChildren();
    optionBox.hidden = !options.length;
    if (search) {
      search.disabled = !options.length;
      search.value = '';
    }
    if (options.length) {
      const resetLabel = document.createElement('label');
      const resetInput = document.createElement('input');
      const resetSpan = document.createElement('span');
      resetInput.type = 'radio';
      resetInput.name = `matte_${field}`;
      resetInput.value = '';
      resetInput.dataset.matteField = field;
      resetInput.dataset.matteValue = '';
      resetInput.checked = selectedValue === '';
      resetSpan.textContent = 'All · Reset';
      resetLabel.dataset.matteFilterValue = 'all reset';
      resetLabel.append(resetInput, resetSpan);
      optionBox.append(resetLabel);
    }
    options.forEach(option => {
      const label = document.createElement('label');
      const input = document.createElement('input');
      const span = document.createElement('span');
      const value = field === 'model' ? option.key : option;
      const text = field === 'model' ? labelForModel(option) : option;
      const count = field === 'model' ? modelResultCount(state, option, config(state)) :
        (optionData?.rowCount?.get(option) || 0);
      input.type = 'radio';
      input.name = `matte_${field}`;
      input.value = value;
      input.dataset.matteField = field;
      input.dataset.matteValue = value;
      if (field === 'model') {
        input.dataset.matteBrand = option.brand;
        input.dataset.matteModel = option.group;
        input.dataset.matteModelValues = JSON.stringify(option.values);
      }
      input.checked = selectedValue === value;
      span.textContent = `${text}${count ? ` (${count})` : ''}`;
      label.dataset.matteFilterValue = text.toLocaleLowerCase();
      label.append(input, span);
      optionBox.append(label);
    });
  };

  const compactOptionValue = (field, option) => field === 'model' ? option.key : option;
  const compactOptionLabel = (field, option) => field === 'model' ? (option.label || option.group) : option;

  const renderCompactOptions = (state, control, options, selectedValue, field, countFor) => {
    const optionBox = control?.querySelector('[data-matte-options]');
    const search = control?.querySelector('[data-matte-filter-search]');
    if (!optionBox) return;
    optionBox.replaceChildren();
    optionBox.hidden = !options.length;
    if (search) {
      search.disabled = !options.length;
      search.value = '';
    }
    if (options.length) {
      const resetLabel = document.createElement('label');
      const resetInput = document.createElement('input');
      const resetSpan = document.createElement('span');
      resetInput.type = 'radio';
      resetInput.name = `matte_${field}`;
      resetInput.value = '';
      resetInput.dataset.matteField = field;
      resetInput.dataset.matteValue = '';
      resetInput.checked = selectedValue === '';
      resetSpan.textContent = 'All · Reset';
      resetLabel.dataset.matteFilterValue = 'all reset';
      resetLabel.append(resetInput, resetSpan);
      optionBox.append(resetLabel);
    }
    options.forEach(option => {
      const label = document.createElement('label');
      const input = document.createElement('input');
      const span = document.createElement('span');
      const value = compactOptionValue(field, option);
      const text = compactOptionLabel(field, option);
      const count = countFor(option);
      input.type = 'radio';
      input.name = `matte_${field}`;
      input.value = value;
      input.dataset.matteField = field;
      input.dataset.matteValue = value;
      if (field === 'model') {
        input.dataset.matteBrand = option.brand || '';
        input.dataset.matteModel = option.group || '';
        input.dataset.matteModelValues = JSON.stringify(option.values || []);
      }
      input.checked = selectedValue === value;
      span.textContent = `${text}${count ? ` (${count})` : ''}`;
      label.dataset.matteFilterValue = asString(text).toLocaleLowerCase();
      label.append(input, span);
      optionBox.append(label);
    });
  };

  const compactControlValue = (selection, field) => {
    if (field === 'brand' || field === 'logo') return selection?.[field] || '';
    return selection?.modelOption?.key || '';
  };

  const compactControlLabel = (selection, field) => {
    if (field === 'brand' || field === 'logo') return selection?.[field] || '';
    return selection?.modelOption?.label || selection?.modelOption?.group || '';
  };

  const renderCompactControl = (state, control, field, options, selection, rows) => {
    if (!control) return;
    const selectedValue = compactControlValue(selection, field);
    const summaryValue = compactControlLabel(selection, field);
    const caption = control.querySelector('[data-matte-selected]');
    const summary = control.querySelector('summary');
    const panel = control.querySelector('.matte-filter-panel');
    const controlState = !options.length ? 'unavailable' :
      field !== 'brand' && options.length === 1 && selectedValue === compactOptionValue(field, options[0]) ? 'fixed' : 'selectable';
    control.hidden = false;
    if (caption) {
      caption.textContent = summaryValue || (controlState === 'unavailable' ? 'Unavailable' : `Select ${field}`);
      caption.title = caption.textContent;
    }
    setSummaryState(control, controlState, controlState !== 'selectable');
    if (panel) panel.hidden = controlState !== 'selectable';
    if (controlState === 'selectable') {
      renderCompactOptions(state, control, options, selectedValue, field,
        option => field === 'brand' && state.brandDirectory ? state.brandDirectory.get(option)?.count || 0 :
          compactCount(rows, field === 'model' ? { ...selection, hasLogo: false } : selection, field, option));
    } else {
      renderCompactOptions(state, control, [], selectedValue, field, () => 0);
    }
    if (summary) summary.setAttribute('aria-label', `${field[0].toUpperCase()}${field.slice(1)}${summaryValue ? `: ${summaryValue}` : ''}`);
  };

  const renderControl = (state, control, options) => {
    if (!control) return;
    const field = control.dataset.matteControl;
    const selected = state.selection;
    const selectedOption = selected?.modelOption || null;
    const selectedValue = field === 'model' ? selectedOption?.key || '' : selected?.logo || '';
    const summaryValue = field === 'model' ? labelForModel(selectedOption) : selected?.logo || '';
    const caption = control.querySelector('[data-matte-selected]');
    const summary = control.querySelector('summary');
    const panel = control.querySelector('.matte-filter-panel');
    const settings = config(state);
    let visible = true;
    let available = options;
    let controlState = 'selectable';
    if (field === 'model') {
      visible = !settings.modelScope && options.length > 1;
      if (!options.length) controlState = 'unavailable';
      else if (!visible) controlState = 'fixed';
    } else {
      visible = !(settings.modelScope && options.length <= 1);
      if (!options.length) controlState = 'unavailable';
      else if (options.length === 1) controlState = 'fixed';
    }
    control.hidden = !visible;
    if (caption) {
      caption.textContent = summaryValue || (controlState === 'unavailable' ? 'Unavailable' :
        field === 'model' ? 'Select model' : 'Select logo');
      caption.title = caption.textContent;
    }
    setSummaryState(control, controlState, controlState !== 'selectable');
    if (panel) panel.hidden = controlState !== 'selectable';
    if (controlState === 'selectable') {
      const logoRows = selectedOption ? rowsForOption(state.metadata.rows, selectedOption, settings.brand) : state.metadata.rows;
      const counts = field === 'logo' ? new Map(available.map(value => [value,
        logoRows.filter(row => row.logos.includes(value)).length])) : null;
      renderOptions(state, control, available, selectedValue, field, { rowCount: counts });
    } else {
      renderOptions(state, control, [], '', field);
    }
    if (summary) summary.setAttribute('aria-label', `${field === 'model' ? 'Model' : 'Logo'}${summaryValue ? `: ${summaryValue}` : ''}`);
  };

  const finishLinkContainer = state => state.root?.closest('[id^="shopify-section-"]') || document;

  const updateFinishLinks = state => {
    if (!config(state).compactFinishCatalog) return;
    const current = currentURLFor(state);
    finishLinkContainer(state).querySelectorAll('[data-finish-catalog-link]').forEach(link => {
      const base = link.dataset.finishCatalogBase || link.getAttribute('href') || link.href;
      if (!base) return;
      link.dataset.finishCatalogBase = base;
      const url = new URL(base, window.location.href);
      [BRAND_KEY, MODEL_KEY, LOGO_KEY].forEach(key => url.searchParams.delete(key));
      [BRAND_KEY, MODEL_KEY, LOGO_KEY].forEach(key => {
        current.searchParams.getAll(key).forEach(value => url.searchParams.append(key, value));
      });
      if (current.searchParams.has('sort_by')) url.searchParams.set('sort_by', current.searchParams.get('sort_by'));
      else url.searchParams.delete('sort_by');
      url.searchParams.delete('page');
      if (current.searchParams.has('preview_theme_id') && !url.searchParams.has('preview_theme_id')) {
        url.searchParams.set('preview_theme_id', current.searchParams.get('preview_theme_id'));
      }
      const targetPath = pathFor(url.pathname);
      if (COMPACT_COLLECTION_PATHS.has(targetPath) && targetPath !== pathFor(current.pathname)) {
        url.searchParams.set(FINISH_SWITCH_KEY, '1');
      } else {
        url.searchParams.delete(FINISH_SWITCH_KEY);
      }
      link.href = url.pathname + url.search + url.hash;
    });
  };

  const compactResetURL = state => {
    const url = currentURLFor(state);
    for (const key of [...url.searchParams.keys()]) if (key.startsWith('filter.')) url.searchParams.delete(key);
    url.searchParams.delete(LEGACY_MODEL_KEY);
    LEGACY_PAGE_KEYS.forEach(key => url.searchParams.delete(key));
    url.searchParams.delete(FINISH_SWITCH_KEY);
    url.searchParams.delete('page');
    return url;
  };

  const readBrandDirectory = root => {
    const node = root?.querySelector('[data-matte-server-brands]');
    const data = JSON.parse(node?.textContent || '{"values":[]}');
    if (!Array.isArray(data.values)) throw new Error('Invalid brand directory');
    const result = new Map();
    data.values.filter(item => Number(item.count) > 0 && asString(item.value)).forEach(item => {
      const name = canonicalCompactBrand(item.value);
      const existing = result.get(name);
      result.set(name, {
        values: unique([...(existing?.values || []), item.value]),
        count: (existing?.count || 0) + Number(item.count)
      });
    });
    return new Map([...result].sort(([a], [b]) => a.localeCompare(b)));
  };

  const brandDirectoryURL = state => {
    const url = metadataBaseURL(state);
    url.searchParams.delete(BRAND_KEY);
    url.searchParams.delete('page');
    url.searchParams.set('section_id', 'matte-brand-values');
    return url;
  };

  const loadBrandDirectory = async (state, token) => {
    const url = brandDirectoryURL(state);
    const cached = brandDirectoryCache.get(url.href);
    if (cached) { state.brandDirectory = cached; return; }
    const current = state.currentURL;
    const filtered = [...current.searchParams.keys()].some(key => key.startsWith('filter.'));
    if (!filtered) {
      let directory;
      try { directory = readBrandDirectory(state.form); } catch (_) { directory = new Map(); }
      if (directory.size) {
        state.brandDirectory = directory;
        brandDirectoryCache.set(url.href, directory);
        return;
      }
    }
    const controller = new AbortController();
    state.brandDirectoryController = controller;
    const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal });
    if (!response.ok) throw new Error(`Brand directory response ${response.status}`);
    const fragment = new DOMParser().parseFromString(await response.text(), 'text/html');
    const directory = readBrandDirectory(fragment);
    if (!directory.size) throw new Error('Missing server brand options');
    if (state.destroyed || token !== state.compactRequest || controller.signal.aborted) return;
    state.brandDirectory = directory;
    brandDirectoryCache.set(url.href, directory);
  };

  const renderCompact = state => {
    if (!state.form) return;
    const rows = state.metadata?.rows || [];
    const selection = resolveCompactSelection(rows, state.currentURL);
    state.selection = selection;
    const modelRows = compactRowsForSelection(rows, { ...selection, hasModel: false, hasLogo: false });
    const models = buildModelOptions(modelRows, { globalCatalog: true, compactMercedesAliases: true });
    const logos = sortedValues(compactRowsForSelection(rows, { ...selection, hasLogo: false }).flatMap(row => row.logos));
    renderCompactControl(state, state.form.querySelector('[data-matte-control="brand"]'), 'brand', [...(state.brandDirectory?.keys() || [])], selection, rows);
    for (const [field, options] of [['model', models], ['logo', logos]]) {
      const control = state.form.querySelector(`[data-matte-control="${field}"]`);
      renderCompactControl(state, control, field, state.ready ? options : [], selection, rows);
      if (!selection.hasBrand || !state.ready) {
        const caption = control?.querySelector('[data-matte-selected]');
        if (caption) caption.textContent = compactControlLabel(selection, field) || `Select ${field}`;
        setSummaryState(control, selection.hasBrand ? 'loading' : 'unavailable', true);
      }
    }
    const reset = state.root.querySelector('[data-matte-reset]');
    if (reset) {
      const url = compactResetURL(state);
      reset.href = url.pathname + url.search + url.hash;
      reset.hidden = !(selection.hasBrand || selection.hasModel || selection.hasLogo);
    }
    updateFinishLinks(state);
  };

  const compactSingletonURL = state => {
    let url = new URL(state.currentURL.href);
    const rows = state.metadata.rows;
    if (url.searchParams.has(FINISH_SWITCH_KEY)) url = compactCanonicalizeFinishSwitchURL(url, rows);
    let selection = resolveCompactSelection(rows, url);
    if (!selection.hasBrand || !rows.length) return url;
    const options = buildModelOptions(rows, { globalCatalog: true, compactMercedesAliases: true });
    if (!selection.hasModel && options.length === 1) {
      compactNativeModelValues(options[0]).forEach(value => url.searchParams.append(MODEL_KEY, value));
      selection = resolveCompactSelection(rows, url);
    }
    if (!selection.hasLogo && selection.logos.length === 1) url.searchParams.set(LOGO_KEY, selection.logos[0]);
    return url;
  };

  const startCompactMetadata = state => {
    if (state.compactInitialized) return state.metadataPromise;
    state.compactInitialized = true;
    const token = ++state.compactRequest;
    state.metadataController?.abort();
    state.brandDirectoryController?.abort();
    state.metadata = null;
    state.ready = false;
    if (!state.brandDirectory) {
      try { state.brandDirectory = readBrandDirectory(state.form); }
      catch (_) { state.brandDirectory = new Map(); }
    }
    renderCompact(state);
    const directory = loadBrandDirectory(state, token).then(() => {
      if (!state.destroyed && token === state.compactRequest) renderCompact(state);
    }).catch(error => {
      if (state.destroyed || token !== state.compactRequest || error.name === 'AbortError') return;
      setStatus(state, 'error', 'Vehicle options could not be loaded.', brandDirectoryURL(state), 'metadata');
    });
    if (!compactBrandParams(state.currentURL).length) {
      const hasFitment = state.currentURL.searchParams.has(MODEL_KEY) || state.currentURL.searchParams.has(LOGO_KEY);
      let initialDirectory;
      try { initialDirectory = readBrandDirectory(state.form); } catch (_) { initialDirectory = new Map(); }
      if (hasFitment && initialDirectory.size === 1) {
        const next = new URL(state.currentURL.href);
        initialDirectory.values().next().value.values.forEach(value => next.searchParams.append(BRAND_KEY, value));
        return navigate(state, next, false);
      }
      state.ready = true;
      renderCompact(state);
      setStatus(state, 'ready', '');
      return directory;
    }
    setPending(state);
    const cacheKey = metadataCacheKeyFor(state);
    const cached = brandRowsCache.get(cacheKey);
    let task = brandRequests.get(cacheKey);
    if (task?.controller.signal.aborted) task = null;
    if (!cached && !task) {
      const request = fetchMetadata(state);
      const controller = state.metadataController;
      const clear = () => { if (brandRequests.get(cacheKey)?.controller === controller) brandRequests.delete(cacheKey); };
      const promise = request.then(metadata => {
        clear();
        if (!controller.signal.aborted) {
          brandRowsCache.set(cacheKey, metadata);
          if (brandRowsCache.size > 12) brandRowsCache.delete(brandRowsCache.keys().next().value);
        }
        return metadata;
      }, error => { clear(); throw error; });
      task = { controller, promise };
      brandRequests.set(cacheKey, task);
    }
    if (task) state.metadataController = task.controller;
    const promise = cached ? Promise.resolve(cached) : task.promise;
    state.metadataPromise = promise;
    promise.then(async metadata => {
      if (state.destroyed || token !== state.compactRequest || !state.root.isConnected) return;
      brandRowsCache.set(cacheKey, metadata);
      if (brandRowsCache.size > 12) brandRowsCache.delete(brandRowsCache.keys().next().value);
      state.metadata = metadata;
      state.ready = true;
      renderCompact(state);
      setStatus(state, 'ready', '');
      const next = state.autoSingleton ? compactSingletonURL(state) : new URL(state.currentURL.href);
      if (componentURLChanged(state.currentURL, next)) await navigate(state, next, false);
    }).catch(error => {
      if (state.destroyed || token !== state.compactRequest || error.name === 'AbortError') return;
      state.ready = false;
      renderCompact(state);
      setStatus(state, 'error', 'Vehicle options could not be loaded.', metadataBaseURL(state), 'metadata');
    });
    return promise;
  };

  const render = state => {
    if (state?.form && config(state).compactFinishCatalog) return renderCompact(state);
    if (!state?.form || !state.metadata) return;
    const settings = config(state);
    const options = buildModelOptions(state.metadata.rows, settings);
    const selection = resolveSelection(state.metadata.rows, options, {
      globalCatalog: settings.globalCatalog,
      modelScope: settings.modelScope,
      brand: settings.brand,
      autoSingleton: true,
      fixedBrand: true
    }, state.currentURL);
    state.selection = selection;
    const modelOptions = options;
    const logoOptions = selection.logos;
    renderControl(state, state.form.querySelector('[data-matte-control="model"]'), modelOptions);
    renderControl(state, state.form.querySelector('[data-matte-control="logo"]'), logoOptions);
    const reset = state.root.querySelector('[data-matte-reset]');
    if (reset) {
      const resetURL = canonicalizeURL(state.currentURL, { modelOption: null, logo: '', logos: [] }, settings);
      reset.href = resetURL.pathname + resetURL.search + resetURL.hash;
      reset.hidden = !(selection.modelOption || selection.logo || state.currentURL.searchParams.has(BRAND_KEY));
    }
    state.form.querySelectorAll('[data-matte-filter-search]').forEach(input => { input.disabled = false; });
    setStatus(state, 'ready', '');
  };

  const syncURLAfterMetadata = state => {
    if (state.resultController || state.pendingURL) return Promise.resolve();
    const current = state.currentURL || new URL(window.location.href);
    const settings = config(state);
    if (settings.compactFinishCatalog) {
      if (!current.searchParams.has(FINISH_SWITCH_KEY)) {
        updateFinishLinks(state);
        return Promise.resolve();
      }
      const next = compactCanonicalizeFinishSwitchURL(current, state.metadata.rows);
      if (!componentURLChanged(current, next)) return Promise.resolve();
      state.currentURL = next;
      window.history.replaceState({}, '', next.href);
      return navigate(state, next, false, { skipCanonical: true });
    }
    const next = canonicalizeURL(current, state.selection, settings);
    if (!componentURLChanged(current, next)) return Promise.resolve();
    state.currentURL = next;
    window.history.replaceState({}, '', next.href);
    return navigate(state, next, false, { skipCanonical: true });
  };

  const metadataCacheKeyFor = state => {
    const base = metadataBaseURL(state);
    return base.origin + base.pathname + '?' + base.searchParams.toString();
  };

  const observeMetadata = (state, promise, cacheKey) => {
    if (!state || state.metadataObserved === promise) return promise;
    state.metadataObserved = promise;
    state.metadataPromise = promise;
    state.metadataCacheKey = cacheKey;
    promise.then(metadata => {
      if (state.destroyed || !state.root.isConnected) return;
      state.metadata = metadata;
      state.ready = true;
      state.metadataController = null;
      render(state);
      return syncURLAfterMetadata(state);
    }).catch(error => {
      if (metadataCache.get(cacheKey) === promise) metadataCache.delete(cacheKey);
      if (state.destroyed || !state.root.isConnected || error?.name === 'AbortError') return;
      state.metadataPromise = null;
      state.metadataObserved = null;
      state.metadataCacheKey = '';
      state.ready = false;
      setPending(state);
      setStatus(state, 'error', 'Vehicle options could not be loaded.', metadataBaseURL(state), 'metadata');
    });
    return promise;
  };

  const startMetadata = state => {
    if (!state) return null;
    if (config(state).compactFinishCatalog) return startCompactMetadata(state);
    if (state.metadataPromise) return observeMetadata(state, state.metadataPromise, state.metadataCacheKey || metadataCacheKeyFor(state));
    setPending(state);
    const cacheKey = metadataCacheKeyFor(state);
    const cached = metadataCache.get(cacheKey);
    const promise = cached || fetchMetadata(state);
    if (!cached) metadataCache.set(cacheKey, promise);
    return observeMetadata(state, promise, cacheKey);
  };

  const init = (target, carry = null) => {
    const catalog = isElement(target) && target.matches(CATALOG_SELECTOR) ? target :
      (isElement(target) ? target.querySelector(CATALOG_SELECTOR) : document.querySelector(CATALOG_SELECTOR));
    if (!catalog) return null;
    const state = ensureState(catalog, carry);
    if (!state) return null;
    state.form = catalog.querySelector('[data-matte-filters]');
    if (!state.form) return state;
    if (config(state).compactFinishCatalog) {
      startCompactMetadata(state);
    } else if (state.metadata) {
      render(state);
    } else {
      startMetadata(state);
    }
    return state;
  };

  const requestURL = (state, target) => {
    const url = target instanceof URL ? new URL(target.href) : new URL(target, window.location.href);
    const sectionId = config(state).sectionId;
    if (sectionId) url.searchParams.set('section_id', sectionId);
    return url;
  };

  const settleDialog = dialog => new Promise(resolve => {
    const idle = () => !dialog.classList.contains('dialog-opening') && !dialog.classList.contains('dialog-closing');
    if (idle()) return resolve();
    const observer = new MutationObserver(() => {
      if (idle()) { observer.disconnect(); resolve(); }
    });
    observer.observe(dialog, { attributes: true, attributeFilter: ['class'] });
    window.setTimeout(() => { observer.disconnect(); resolve(); }, 2000);
  });

  const closeDialogs = async root => {
    for (const dialog of root?.querySelectorAll('dialog') || []) {
      await settleDialog(dialog);
      if (!dialog.isConnected) continue;
      if (dialog.open) {
        try {
          if (typeof dialog.parentElement?.close === 'function') await dialog.parentElement.close(true);
          else dialog.close();
        } catch (_) {
          try { dialog.close(); } catch (_) { /* stale custom dialog */ }
        }
      }
    }
  };

  const errorURL = (state, target) => {
    const url = target instanceof URL ? target : new URL(target, window.location.href);
    return url;
  };

  const navigate = async (state, target, push = true, options = {}) => {
    if (!state?.root?.isConnected) return;
    const targetURL = target instanceof URL ? new URL(target.href) : new URL(target, window.location.href);
    const old = state.root;
    state.pendingURL = targetURL;
    if (config(state).compactFinishCatalog) {
      state.compactRequest++;
      const sameBrand = compactBrandParams(state.currentURL).sort().join('\u0000') === compactBrandParams(targetURL).sort().join('\u0000');
      if (!sameBrand) state.metadataController?.abort();
      state.brandDirectoryController?.abort();
    }
    state.resultController?.abort();
    const controller = new AbortController();
    state.resultController = controller;
    old.setAttribute('aria-busy', 'true');
    setStatus(state, 'loading', '');
    try {
      const response = await fetch(requestURL(state, targetURL), {
        credentials: 'same-origin',
        signal: controller.signal
      });
      if (!response.ok) throw new Error(`Catalog response ${response.status}`);
      const html = await response.text();
      if (state.resultController !== controller || controller.signal.aborted) return;
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const fresh = documentFragment.querySelector(CATALOG_SELECTOR) || documentFragment.querySelector('[data-pilot-catalog]');
      if (!fresh || !fresh.querySelector('[data-pilot-count]')) throw new Error('Invalid catalog response');
      const oldGrid = old.querySelector('[data-pilot-products]');
      const gridStyle = oldGrid?.getAttribute('style') || '';
      await closeDialogs(old);
      if (state.resultController !== controller || controller.signal.aborted || !old.isConnected) return;
      state.destroyed = true;
      old.replaceWith(fresh);
      const freshGrid = fresh.querySelector('[data-pilot-products]');
      if (gridStyle && freshGrid) freshGrid.setAttribute('style', gridStyle);
      if (push) window.history.pushState({}, '', targetURL.href);
      else if (!options.skipHistory) window.history.replaceState({}, '', targetURL.href);
      const next = init(fresh, config(state).compactFinishCatalog
        ? { currentURL: targetURL, brandDirectory: state.brandDirectory, autoSingleton: !options.skipAutoSingleton } : state);
      if (next) {
        next.currentURL = new URL(targetURL.href);
        next.pendingURL = null;
        next.resultController = null;
        next.ready = Boolean(next.metadata) || (config(next).compactFinishCatalog && !compactBrandParams(next.currentURL).length);
        if (next.metadata) render(next);
      }
    } catch (error) {
      if (error?.name === 'AbortError' || state.resultController !== controller) return;
      if (old.isConnected) {
        state.pendingURL = null;
        state.compactInitialized = false;
        old.removeAttribute('aria-busy');
        setStatus(state, 'error', 'The catalog could not be updated.', errorURL(state, targetURL), 'catalog');
      }
    } finally {
      if (state.resultController === controller) state.resultController = null;
    }
  };

  const stopHandled = event => {
    event.preventDefault();
    event.stopImmediatePropagation();
  };

  const currentURLFor = state => new URL((state.pendingURL || state.currentURL || window.location).href);

  const brandTarget = (state, input) => {
    const url = currentURLFor(state);
    url.searchParams.delete('page');
    url.searchParams.delete(FINISH_SWITCH_KEY);
    const value = asString(input.value);
    if (config(state).compactFinishCatalog) {
      url.searchParams.delete(MODEL_KEY);
      url.searchParams.delete(LOGO_KEY);
    }
    url.searchParams.delete(BRAND_KEY);
    if (value) {
      if (config(state).compactFinishCatalog) {
        compactBrandValues(state, value).forEach(native => url.searchParams.append(BRAND_KEY, native));
      } else {
        url.searchParams.set(BRAND_KEY, value);
      }
    }
    return url;
  };

  const modelTarget = (state, input) => {
    const settings = config(state);
    const url = currentURLFor(state);
    url.searchParams.delete('page');
    LEGACY_PAGE_KEYS.forEach(key => url.searchParams.delete(key));
    if (settings.compactFinishCatalog) {
      url.searchParams.delete(FINISH_SWITCH_KEY);
      url.searchParams.delete(MODEL_KEY);
      url.searchParams.delete(LOGO_KEY);
      const option = state.selection?.modelOptions?.find(item => item.key === input.dataset.matteValue);
      if (!option || option.unknown || !input.dataset.matteValue) return url;
      url.searchParams.delete(BRAND_KEY);
      if (option.brand) {
        if (compactAliasGroup(option.brand)) {
          const currentBrands = compactBrandParams(currentURLFor(state));
          const currentCanonical = canonicalCompactBrand(currentBrands[0] || '');
          const values = currentCanonical === canonicalCompactBrand(option.brand) && currentBrands.length
            ? currentBrands
            : compactBrandValues(state, option.brand);
          values.forEach(value => url.searchParams.append(BRAND_KEY, value));
        } else {
          url.searchParams.set(BRAND_KEY, option.brand);
        }
      }
      compactNativeModelValues(option).forEach(value => url.searchParams.append(MODEL_KEY, value));
      return url;
    }
    url.searchParams.delete(MODEL_KEY);
    url.searchParams.delete(BRAND_KEY);
    url.searchParams.delete(LOGO_KEY);
    const option = state.selection?.modelOptions?.find(item => item.key === input.dataset.matteValue);
    if (!option) return url;
    if (option.brand) url.searchParams.set(BRAND_KEY, option.brand);
    (option.acura ? option.values : [option.group]).forEach(value => url.searchParams.append(MODEL_KEY, value));
    return url;
  };

  const logoTarget = (state, input) => {
    const url = currentURLFor(state);
    url.searchParams.delete('page');
    LEGACY_PAGE_KEYS.forEach(key => url.searchParams.delete(key));
    if (config(state).compactFinishCatalog) url.searchParams.delete(FINISH_SWITCH_KEY);
    const value = asString(input.value);
    if (value) url.searchParams.set(LOGO_KEY, value);
    else url.searchParams.delete(LOGO_KEY);
    return url;
  };

  const resetTarget = state => {
    const url = currentURLFor(state);
    for (const key of [...url.searchParams.keys()]) if (key.startsWith('filter.')) url.searchParams.delete(key);
    url.searchParams.delete(MODEL_KEY);
    url.searchParams.delete(BRAND_KEY);
    url.searchParams.delete(LOGO_KEY);
    url.searchParams.delete(LEGACY_MODEL_KEY);
    LEGACY_PAGE_KEYS.forEach(key => url.searchParams.delete(key));
    url.searchParams.delete(FINISH_SWITCH_KEY);
    url.searchParams.delete('page');
    return url;
  };

  const preserveComponentFilters = (state, target) => {
    const url = target instanceof URL ? new URL(target.href) : new URL(target, window.location.href);
    const current = currentURLFor(state);
    [MODEL_KEY, BRAND_KEY, LOGO_KEY].forEach(key => {
      if (!url.searchParams.has(key)) current.searchParams.getAll(key).forEach(value => url.searchParams.append(key, value));
    });
    return url;
  };

  const handleChange = event => {
    const input = event.target?.closest?.('[data-matte-field]');
    const catalog = closestCatalog(event.target);
    if (!input || !catalog) return;
    const state = ensureState(catalog);
    const brandChange = state && config(state).compactFinishCatalog && input.dataset.matteField === 'brand';
    if ((!state?.ready && !brandChange) || input.disabled) { stopHandled(event); return; }
    stopHandled(event);
    if (config(state).compactFinishCatalog && input.dataset.matteField === 'brand') navigate(state, brandTarget(state, input));
    else if (input.dataset.matteField === 'model') navigate(state, modelTarget(state, input));
    else if (input.dataset.matteField === 'logo') navigate(state, logoTarget(state, input));
  };

  const handleInput = event => {
    const input = event.target?.closest?.('[data-matte-filter-search]');
    if (!input) return;
    const panel = input.closest('.matte-filter-panel');
    const query = lower(input.value);
    panel?.querySelectorAll('[data-matte-filter-value]').forEach(label => {
      label.hidden = query !== '' && !lower(label.dataset.matteFilterValue || label.textContent).includes(query);
    });
  };

  const handleClick = event => {
    const catalog = closestCatalog(event.target);
    if (!catalog) return;
    const state = ensureState(catalog);
    if (!state) return;
    if (suppressedSummary) {
      const summary = event.target.closest('.matte-filter-list summary');
      if (summary === suppressedSummary) {
        suppressedSummary = null;
        stopHandled(event);
        return;
      }
      suppressedSummary = null;
    }
    const retry = event.target.closest('[data-matte-retry]');
    if (retry && retry.closest(CATALOG_SELECTOR) === catalog) {
      stopHandled(event);
      const retryURL = retry.dataset.matteRetryUrl ? new URL(retry.dataset.matteRetryUrl, window.location.href) : null;
      if (retry.dataset.matteRetryKind === 'catalog') {
        if (retryURL) navigate(state, retryURL);
        return;
      }
      state.metadataPromise = null;
      state.metadataObserved = null;
      state.metadataCacheKey = '';
      state.metadata = null;
      state.ready = false;
      state.compactInitialized = false;
      if (retryURL && !state.resultController) {
        startMetadata(state);
      }
      return;
    }
    const summary = event.target.closest('.matte-filter-list summary');
    if (summary && summary.closest(CATALOG_SELECTOR) === catalog && summary.getAttribute('aria-disabled') === 'true') {
      stopHandled(event);
      return;
    }
    const reset = event.target.closest('[data-matte-reset],[data-pilot-clear]');
    if (reset && reset.closest(CATALOG_SELECTOR) === catalog) {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      stopHandled(event);
      navigate(state, resetTarget(state));
      return;
    }
    const sortOption = event.target.closest('.hdt-control-sorting [role="option"]');
    if (sortOption && sortOption.closest(CATALOG_SELECTOR) === catalog) {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      stopHandled(event);
      const url = currentURLFor(state);
      url.searchParams.set('sort_by', sortOption.value || sortOption.getAttribute('value') || '');
      url.searchParams.delete('page');
      navigate(state, url);
      return;
    }
    const pageLink = event.target.closest('.hdt-pagination a');
    if (pageLink && pageLink.closest(CATALOG_SELECTOR) === catalog) {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      stopHandled(event);
      navigate(state, preserveComponentFilters(state, new URL(pageLink.href, window.location.href)));
    }
  };

  const handleKeydown = event => {
    const summary = event.target?.closest?.('.matte-filter-list summary');
    if (summary && summary.getAttribute('aria-disabled') === 'true' && (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar')) {
      stopHandled(event);
      return;
    }
    if (event.key === 'Escape') {
      const detail = event.target?.closest?.('.matte-filter-list[open]');
      if (detail) {
        event.preventDefault();
        detail.open = false;
        detail.querySelector('summary')?.focus();
      }
    }
  };

  const handlePointerdown = event => {
    suppressedSummary = null;
    if (event.pointerType === 'mouse') return;
    const summary = event.target?.closest?.('.matte-filter-list summary');
    if (!summary || !summary.closest(CATALOG_SELECTOR)) return;
    touchGesture = { id: event.pointerId, summary, x: event.clientX, y: event.clientY, moved: false };
  };

  const handlePointermove = event => {
    if (!touchGesture || touchGesture.id !== event.pointerId) return;
    const dx = event.clientX - touchGesture.x;
    const dy = event.clientY - touchGesture.y;
    if (Math.max(Math.abs(dx), Math.abs(dy)) > 8) touchGesture.moved = true;
  };

  const handlePointerup = event => {
    if (!touchGesture || touchGesture.id !== event.pointerId) return;
    if (touchGesture.moved) suppressedSummary = touchGesture.summary;
    touchGesture = null;
  };

  const handlePointercancel = event => {
    if (!touchGesture || touchGesture.id !== event.pointerId) return;
    suppressedSummary = touchGesture.summary;
    touchGesture = null;
  };

  const handlePopstate = () => {
    document.querySelectorAll(CATALOG_SELECTOR).forEach(catalog => {
      const state = ensureState(catalog);
      if (state) navigate(state, new URL(window.location.href), false, { skipAutoSingleton: true });
    });
  };

  const bindListeners = () => {
    if (listenersBound) return;
    listenersBound = true;
    window.addEventListener('change', handleChange, true);
    window.addEventListener('input', handleInput, true);
    window.addEventListener('click', handleClick, true);
    window.addEventListener('keydown', handleKeydown, true);
    window.addEventListener('pointerdown', handlePointerdown, { capture: true, passive: true });
    window.addEventListener('pointermove', handlePointermove, { capture: true, passive: true });
    window.addEventListener('pointerup', handlePointerup, { capture: true, passive: true });
    window.addEventListener('pointercancel', handlePointercancel, { capture: true, passive: true });
    window.addEventListener('popstate', handlePopstate);
  };

  const boot = () => {
    bindListeners();
    document.querySelectorAll(CATALOG_SELECTOR).forEach(catalog => init(catalog));
  };

  const api = window.ldsMatteCatalog || {};
  api.init = init;
  api.render = stateOrRoot => {
    const state = stateOrRoot?.root ? stateOrRoot : ensureState(stateOrRoot);
    if (state) render(state);
    return state;
  };
  api.navigate = navigate;
  api.helpers = {
    normalizeRow,
    normalizeRows,
    buildModelOptions,
    allLogos,
    rowsForOption,
    resolveSelection,
    canonicalizeURL,
    compactBrandOptions,
    compactRowsForSelection,
    compactSelectionOptions,
    resolveCompactSelection,
    compactCanonicalizeFinishSwitchURL,
    modelGroup,
    constants: { PAGE_SIZE, MODEL_KEY, BRAND_KEY, LOGO_KEY, FINISH_SWITCH_KEY }
  };
  window.ldsMatteCatalog = api;
  window.LdsMatteCatalog = api;

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
